/* eslint-disable @typescript-eslint/no-explicit-any */
import { Role } from './roles';
import { Permission } from './rules/permission';
const canEditZoo = (userZooId, habitatZooId) => userZooId === habitatZooId;
const canEditSchedules = (allowedHabitatIds, habitatId, currentHabitatId) => {
    if (habitatId) {
        return allowedHabitatIds.includes(habitatId);
    }
    return allowedHabitatIds.includes(currentHabitatId);
};
const canEditHabitat = (habitatIds, habitatId) => habitatIds.includes(habitatId);
const canSeeRedeem = (logged) => logged;
const canSeeAllHabitats = (role, active) => {
    if ([Role.ADMIN, Role.PARTNER, Role.HOST, Role.MODERATOR].includes(role)) {
        return true;
    }
    if (role === Role.VIP && active) {
        return true;
    }
    if (role === Role.USER) {
        return active;
    }
    return false;
};
export const dynamicRules = {
    canEditZoo,
    canEditSchedules,
    canEditHabitat,
    canSeeRedeem,
    canSeeAllHabitats,
};
export const staticRules = {
    guest: [Permission.Checkout.Plans, Permission.Signup.View, Permission.Login.View],
    [Role.USER]: [
        Permission.Subscription.Edit,
        Permission.Profile.Edit,
        Permission.Redirect.View,
        Permission.Welcome.View,
        Permission.Checkout.Plans,
        Permission.Map.View,
        Permission.Favorite.Edit,
        Permission.Habitat.View,
        Permission.MyAlbum.View,
        Permission.Schedule.View,
        Permission.Chat.Report,
        Permission.Habitat.ViewCount,
        Permission.EmailVerifyInput.View,
        Permission.FreemiumOnboarding.View,
    ],
    [Role.VIP]: [
        Permission.Subscription.Edit,
        Permission.Profile.Edit,
        Permission.Favorite.Edit,
        Permission.Map.View,
        Permission.MyAlbum.View,
        Permission.Redirect.View,
        Permission.Schedule.View,
        Permission.Chat.Report,
        Permission.Checkout.Plans,
        Permission.Redeem.View,
        Permission.Habitat.View,
        Permission.Habitat.ViewCount,
        Permission.FreemiumOnboarding.View,
    ],
    [Role.PARTNER]: [
        Permission.Habitat.View,
        Permission.Habitat.ViewCount,
        Permission.Habitat.UploadMedia,
        Permission.Map.View,
        Permission.Profile.Edit,
        Permission.Favorite.Edit,
        Permission.MyAlbum.View,
        Permission.Redirect.View,
        Permission.Schedule.View,
        Permission.Schedule.Share,
        Permission.Chat.Report,
        Permission.Chat.MessageTime,
        Permission.Chat.Moderate,
        Permission.Chat.Pin,
        Permission.Media.Download,
        Permission.Camera.Block,
        Permission.Zoo.ViewPartnerStatisticsLink,
        Permission.Habitat.EditStreamDefaultCamera,
    ],
    [Role.HOST]: [
        Permission.Habitat.View,
        Permission.Habitat.ViewCount,
        Permission.Habitat.UploadMedia,
        Permission.Map.View,
        Permission.Profile.Edit,
        Permission.Favorite.Edit,
        Permission.MyAlbum.View,
        Permission.Redirect.View,
        Permission.Schedule.View,
        Permission.Schedule.Share,
        Permission.Chat.Report,
        Permission.Media.Download,
        Permission.Habitat.EditStreamDefaultCamera,
    ],
    [Role.MODERATOR]: [
        Permission.Map.View,
        Permission.Map.AllHabitats,
        Permission.Profile.Edit,
        Permission.Favorite.Edit,
        Permission.MyAlbum.View,
        Permission.Redirect.View,
        Permission.Schedule.View,
        Permission.Habitat.View,
        Permission.Habitat.ViewCount,
        Permission.Habitat.EditStream,
        Permission.Habitat.EditStreamConfiguration,
        Permission.Habitat.EditStreamConfigurationControls,
        Permission.Habitat.EditStreamDefaultCamera,
        Permission.Habitat.EditAlbumMedia,
        Permission.Habitat.EditAlbumMediaHide,
        Permission.Chat.Report,
        Permission.Chat.MessageTime,
        Permission.Chat.Moderate,
        Permission.Chat.Block,
        Permission.Chat.Pin,
        Permission.Camera.Block,
    ],
};
export const rules = ({ canEditZoo, canEditSchedules, canEditHabitat, canSeeRedeem, canSeeAllHabitats, }) => ({
    guest: {
        static: staticRules.guest,
    },
    [Role.USER]: {
        static: staticRules.user,
        dynamic: {
            [Permission.Redeem.View]: canSeeRedeem,
            [Permission.Map.AllHabitats]: canSeeAllHabitats,
        },
    },
    [Role.VIP]: {
        static: staticRules.vip,
        dynamic: {
            [Permission.Map.AllHabitats]: canSeeAllHabitats,
        },
    },
    [Role.PARTNER]: {
        static: staticRules.partner,
        dynamic: {
            [Permission.Zoo.EditTimezone]: canEditZoo,
            [Permission.Zoo.EditLocation]: canEditZoo,
            [Permission.Habitat.Broadcast]: canEditZoo,
            [Permission.Habitat.EditText]: canEditZoo,
            [Permission.Habitat.EditMedia]: canEditZoo,
            [Permission.Habitat.EditCards]: canEditZoo,
            [Permission.Habitat.EditQuiz]: canEditZoo,
            [Permission.Habitat.EditAlbum]: canEditZoo,
            [Permission.Habitat.EditSchedule]: canEditZoo,
            [Permission.Habitat.EditAlbumMedia]: canEditZoo,
            [Permission.Habitat.EditAlbumMediaDownload]: canEditZoo,
            [Permission.Habitat.EditAlbumMediaHide]: canEditZoo,
            [Permission.Habitat.EditStream]: canEditZoo,
            [Permission.Habitat.EditStreamPower]: canEditZoo,
            [Permission.Habitat.EditStreamEmoji]: canEditZoo,
            [Permission.Habitat.EditStreamOverlay]: canEditZoo,
            [Permission.Habitat.EditStreamConfiguration]: canEditZoo,
            [Permission.Habitat.EditStreamConfigurationControls]: canEditZoo,
            [Permission.Habitat.EditStreamConfigurationHabitatMetadata]: canEditZoo,
            [Permission.Habitat.EditStreamOfflineImage]: canEditZoo,
            [Permission.Map.AllHabitats]: canSeeAllHabitats,
        },
    },
    [Role.HOST]: {
        static: staticRules.host,
        dynamic: {
            [Permission.Map.AllHabitats]: canSeeAllHabitats,
            [Permission.Habitat.Broadcast]: canEditHabitat,
            [Permission.Habitat.EditSchedule]: canEditSchedules,
            [Permission.Habitat.EditStream]: canEditHabitat,
            [Permission.Habitat.EditStreamPower]: canEditHabitat,
            [Permission.Habitat.EditStreamConfiguration]: canEditHabitat,
            [Permission.Habitat.EditStreamConfigurationControls]: canEditHabitat,
            [Permission.Habitat.EditStreamConfigurationHabitatMetadata]: canEditHabitat,
            [Permission.Chat.MessageTime]: canEditHabitat,
            [Permission.Chat.Moderate]: canEditHabitat,
            [Permission.Chat.Block]: canEditHabitat,
            [Permission.Chat.Pin]: canEditHabitat,
            [Permission.Camera.Block]: canEditHabitat,
        },
    },
    [Role.MODERATOR]: {
        static: staticRules.moderator,
    },
});
